import { useCallback, useMemo, useState } from 'react';

import { useLoyaltyTransactions } from 'features/loyalty-transactions/use-loyalty-transactions';

import { useLoadMoreAtBottom } from 'components/pull-to-reload/pull-to-reload';
import { useHistoryTransactionHighlight } from 'pages/loyalty/transactions/use-history-transaction-highlight';
import { useLocalizedTransactionTransformer } from 'pages/loyalty/transactions/use-localized-transaction-transformer';

const ROWS_PER_PAGE = 2;
const INITIAL_VISIBLE_TRANSACTIONS = 1;

export const useLoyaltyHistory = () => {
  const transactionTransformer = useLocalizedTransactionTransformer();

  const [visibleTransactions, setVisibleTransactions] = useState(INITIAL_VISIBLE_TRANSACTIONS);
  const [loadingMore, setLoadingMore] = useState(false);

  const {
    transactions,
    initialLoading: transactionsLoading,
    refetch: refetchTransactionHistory,
  } = useLoyaltyTransactions({ legacyFetchPolicy: 'cache-and-network' });

  const tableData = useMemo(() => {
    if (!transactions?.length) {
      return [];
    }

    const filteredTransactions = transactions.filter(transaction => {
      const { pointsEarned, pointsUsed, pointsDeducted, bonusPointsEarned } = transaction;
      return Boolean(bonusPointsEarned || pointsEarned || pointsUsed || pointsDeducted);
    });

    return filteredTransactions.map(transactionTransformer);
  }, [transactions, transactionTransformer]);

  const { highlightedTransactionId, minVisibleMonths } = useHistoryTransactionHighlight(
    tableData,
    !transactionsLoading
  );
  const visibleHistoryMonths = Math.max(visibleTransactions, minVisibleMonths);

  const loadMoreHistoryTransactions = useCallback(() => {
    if (loadingMore) {
      return;
    }

    setLoadingMore(true);
    requestAnimationFrame(() => {
      setVisibleTransactions(prev => prev + ROWS_PER_PAGE);
      setLoadingMore(false);
    });
  }, [loadingMore]);

  const { onScroll } = useLoadMoreAtBottom(loadMoreHistoryTransactions);

  return {
    tableData,
    transactionsLoading,

    visibleTransactions: visibleHistoryMonths,
    highlightedTransactionId,
    loadingMore,
    onScroll,

    refetchTransactionHistory,
    loadMoreHistoryTransactions,
  };
};
